import { defineStore } from 'pinia'
import type { Bubble, WebPageBubble, PreviewOption } from './interfaces'
import { useBubblesStore } from './bubbles'
import { useMovieStore } from '~/stores/core/movieStore'
import { useBookStore } from '~/stores/core/bookStore'
import { usePlaceStore } from '~/stores/core/placeStore'
import { useImageStore } from '~/stores/core/imageStore'
import { useYouTubeStore } from '~/stores/core/youtubeStore'
import { useTextStore } from '~/stores/core/textStore'

export const useBubbleStore = defineStore('bubbleStore', {
  state: () => ({
    bubbleUuid: null as string | null,
    bubble: {} as Bubble | WebPageBubble,
    notes: [] as string[],
    loading: false,
    error: null as string | null,
  }),
  actions: {
    async addBubbleFromUrl(
      type: string,
      url: string
    ): Promise<{ success: boolean; error?: string; bubbleUuid?: string; message?: string }> {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(`${useRuntimeConfig().public.apiBase}/bubble/create/url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ type, url }),
        })
        if (response.status === 200) {
          const data = await response.json()
          return {
            success: true,
            message: 'Bubble created successfully',
            bubbleUuid: data.bubble_uuid,
          }
        } else {
          const errorData = await response.json()
          return {
            success: false,
            error: errorData.detail || 'Failed to add bubble from URL',
          }
        }
      } catch (error) {
        this.error = 'Error adding bubble from URL'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async addBubbleFromText(
      type: string,
      text: string,
      lookup_key?: string,
      title?: string,
      override: boolean = false
    ): Promise<{ success: boolean; error?: string; bubbleUuid?: string; message?: string }> {
      this.loading = true
      this.error = null
      try {
        const payload = { type, text, lookup_key, title, override }
        const response = await fetch(`${useRuntimeConfig().public.apiBase}/bubble/create/text`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        })
        if (response.status === 200) {
          const data = await response.json()
          return {
            success: true,
            message: 'Bubble created successfully',
            bubbleUuid: data.bubble_uuid,
          }
        } else {
          const errorData = await response.json()
          return {
            success: false,
            error: errorData.detail || 'Failed to add bubble from text',
          }
        }
      } catch (error) {
        this.error = 'Error adding bubble from text'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async addBubbleFromImage(
      type: string,
      file: File
    ): Promise<{ success: boolean; error?: string; bubbleUuid?: string; message?: string }> {
      this.loading = true
      this.error = null
      try {
        const formData = new FormData()
        formData.append('type', type)
        formData.append('file', file)

        const response = await fetch(`${useRuntimeConfig().public.apiBase}/bubble/create/image`, {
          method: 'POST',
          body: formData,
        })
        if (response.status === 200) {
          const data = await response.json()
          return {
            success: true,
            message: 'Image bubble created successfully',
            bubbleUuid: data.bubble_uuid,
          }
        } else {
          const errorData = await response.json()
          return {
            success: false,
            error: errorData.detail || 'Failed to add bubble from image',
          }
        }
      } catch (error) {
        this.error = 'Error adding bubble from image'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async addBubbleFromFile(
      type: string,
      file: File
    ): Promise<{ success: boolean; error?: string; bubbleUuid?: string; message?: string }> {
      this.loading = true
      this.error = null
      try {
        const formData = new FormData()
        formData.append('type', type)
        formData.append('file', file)

        const response = await fetch(`${useRuntimeConfig().public.apiBase}/bubble/create/file`, {
          method: 'POST',
          body: formData,
        })
        if (response.status === 200) {
          const data = await response.json()
          return {
            success: true,
            message: 'File bubble created successfully',
            bubbleUuid: data.bubble_uuid,
          }
        } else if (response.status === 501) {
          return {
            success: false,
            error: 'File upload not implemented yet',
          }
        } else {
          const errorData = await response.json()
          return {
            success: false,
            error: errorData.detail || 'Failed to add bubble from file',
          }
        }
      } catch (error) {
        this.error = 'Error adding bubble from file'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },
    async checkUrlExists(
      type: string,
      url: string
    ): Promise<{ exists: boolean; details?: unknown; message: string }> {
      this.loading = true
      this.error = null
      try {
        const payload: { type: string; url: string } = { type, url }

        const response = await fetch(`${useRuntimeConfig().public.apiBase}/bubble/check/url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        return await response.json()
      } catch (error) {
        this.error = 'Error checking URL'
        return { exists: false, message: 'Error checking URL' }
      } finally {
        this.loading = false
      }
    },

    async checkTextExists(
      type: string,
      text: string
    ): Promise<{ exists: boolean; details?: unknown; message: string }> {
      this.loading = true
      this.error = null
      try {
        const payload: { type: string; text: string } = { type, text }

        const response = await fetch(`${useRuntimeConfig().public.apiBase}/bubble/check/text`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        return await response.json()
      } catch (error) {
        this.error = 'Error checking text'
        return { exists: false, message: 'Error checking text' }
      } finally {
        this.loading = false
      }
    },
    async downloadBubble(uuid: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/offline`,
          { method: 'POST' }
        )
        const data = await response.json()
        return { success: response.ok, ...data }
      } catch (error) {
        this.error = 'Error downloading bubble'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },
    async removeDownload(uuid: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/remove_offline`,
          { method: 'DELETE' }
        )
        const data = await response.json()
        return { success: response.ok, ...data }
      } catch (error) {
        this.error = 'Error removing download'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },
    async markAsReviewed(uuid: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/mark_as_reviewed`,
          { method: 'PUT' }
        )
        return await response.json()
      } catch (error) {
        this.error = 'Error marking as reviewed'
      } finally {
        this.loading = false
      }
    },
    async markAsUnreviewed(uuid: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/mark_as_unreviewed`,
          { method: 'PUT' }
        )
        return await response.json()
      } catch (error) {
        this.error = 'Error marking as unreviewed'
      } finally {
        this.loading = false
      }
    },
    async archiveBubble(uuid: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/archive`,
          { method: 'PUT' }
        )
        return await response.json()
      } catch (error) {
        this.error = 'Error archiving bubble'
      } finally {
        this.loading = false
      }
    },
    async unarchiveBubble(uuid: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/unarchive`,
          { method: 'PUT' }
        )
        return await response.json()
      } catch (error) {
        this.error = 'Error unarchiving bubble'
      } finally {
        this.loading = false
      }
    },
    async deleteBubble(uuid: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(`${useRuntimeConfig().public.apiBase}/bubble/${uuid}/delete`, {
          method: 'DELETE',
        })
        const data = await response.json()
        if (response.ok) {
          // Update the bubbles store after successful deletion
          const bubblesStore = useBubblesStore()
          const bubbleToDelete = bubblesStore.bubbles[uuid]
          const bubbleType = bubbleToDelete?.type

          bubblesStore.bubbles = Object.fromEntries(
            Object.entries(bubblesStore.bubbles).filter(([key]) => key !== uuid)
          )
          // Remove from liteBubbles as well
          bubblesStore.liteBubbles = bubblesStore.liteBubbles.filter(
            (bubble) => bubble.uuid !== uuid
          )
          bubblesStore.filterBubblesByType() // This also implicitly removes from bubblesByType

          // --- Update Type-Specific Stores ---
          if (bubbleType === 'movie') {
            const movieStore = useMovieStore()
            movieStore.removeMovie(uuid)
          } else if (bubbleType === 'book') {
            const bookStore = useBookStore()
            bookStore.removeBook(uuid)
          } else if (
            bubbleType === 'bar' ||
            bubbleType === 'hotel' ||
            bubbleType === 'coffee' ||
            bubbleType === 'restaurant' ||
            bubbleType === 'place'
          ) {
            const placeStore = usePlaceStore()
            placeStore.removePlace(uuid)
          } else if (bubbleType === 'image') {
            const imageStore = useImageStore()
            imageStore.removeImage(uuid)
          } else if (bubbleType === 'youtube') {
            const youtubeStore = useYouTubeStore()
            youtubeStore.removeVideo(uuid)
          } else if (bubbleType === 'text') {
            const textStore = useTextStore()
            textStore.removeNote(uuid)
          }
          // Add more else-if blocks for other specific types as needed

          return { success: true, message: data.message }
        } else {
          throw new Error(data.message || 'Failed to delete bubble')
        }
      } catch (error) {
        this.error = 'Error deleting bubble'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },
    async previewBubble(type: string, info: string): Promise<Array<PreviewOption> | null> {
      this.loading = true
      this.error = null
      try {
        const payload: {
          type: string
          info: string
          image_url?: string
        } = {
          type,
          info,
        }

        const response = await fetch(`${useRuntimeConfig().public.apiBase}/preview/${type}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        })

        if (response.status === 200) {
          const data = await response.json()
          return data.map((item: PreviewOption) => ({
            ...item,
            authors: 'authors' in item ? item.authors : undefined,
            address: 'address' in item ? item.address : undefined,
            year: 'year' in item ? item.year : undefined,
            type, // Add this line to include the type in each preview option
          }))
        } else {
          const errorData = await response.json()
          this.error = errorData.message || 'Failed to preview bubble'
          return null
        }
      } catch (error) {
        this.error = 'Error previewing bubble'
        return null
      } finally {
        this.loading = false
      }
    },
    async previewBubbleWithImage(type: string, file: File): Promise<Array<PreviewOption> | null> {
      this.loading = true
      this.error = null
      try {
        const formData = new FormData()
        formData.append('type', type)
        formData.append('image_file', file)

        const response = await fetch(`${useRuntimeConfig().public.apiBase}/preview/image`, {
          method: 'POST',
          body: formData,
        })

        if (response.status === 200) {
          const data = await response.json()
          return data.map((item: PreviewOption) => ({
            ...item,
            authors: 'authors' in item ? item.authors : undefined,
            address: 'address' in item ? item.address : undefined,
            year: 'year' in item ? item.year : undefined,
            type, // Add this line to include the type in each preview option
          }))
        } else {
          const errorData = await response.json()
          this.error = errorData.message || 'Failed to preview bubble with image'
          return null
        }
      } catch (error) {
        this.error = 'Error previewing bubble with image'
        return null
      } finally {
        this.loading = false
      }
    },

    // New method to toggle hidden state
    async toggleHidden(uuid: string) {
      this.loading = true
      this.error = null
      try {
        const endpoint = this.bubble.hidden ? 'unhide' : 'hide'
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/${endpoint}`,
          { method: 'PUT' }
        )
        const data = await response.json()
        if (response.ok) {
          this.bubble.hidden = !this.bubble.hidden
          // Update the bubbles store
          const bubblesStore = useBubblesStore()
          if (bubblesStore.bubbles[uuid]) {
            bubblesStore.bubbles[uuid].hidden = this.bubble.hidden
          }
          return { success: true, ...data }
        } else {
          throw new Error(data.detail || `Failed to ${endpoint} bubble`)
        }
      } catch (e: unknown) {
        const errorMessage = e instanceof Error ? e.message : String(e)
        this.error = `${this.bubble.hidden ? 'Error unhiding' : 'Error hiding'} bubble: ${errorMessage}`
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },
    async changeBubbleTitle(uuid: string, newTitle: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/update_title`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ title: newTitle }),
          }
        )
        const data = await response.json()
        if (response.ok) {
          // Update the local state
          if (this.bubble.uuid === uuid) {
            this.bubble.title = newTitle
          }
          // Update the bubbles store
          const bubblesStore = useBubblesStore()
          if (bubblesStore.bubbles[uuid]) {
            bubblesStore.bubbles[uuid].title = newTitle
          }
          return { success: true, newTitle: data.new_title }
        } else {
          throw new Error(data.detail || 'Failed to update bubble title')
        }
      } catch (error) {
        this.error = 'Error updating bubble title'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },
    async refreshBubbleMetadata(uuid: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/metadata_refresh`,
          { method: 'GET' }
        )
        const data = await response.json()
        if (response.ok) {
          return { success: true, ...data }
        } else {
          throw new Error(data.detail || 'Failed to refresh bubble metadata')
        }
      } catch (error) {
        this.error = 'Error refreshing bubble metadata'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async refreshBubbleFull(uuid: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/full_refresh`,
          { method: 'GET' }
        )
        const data = await response.json()
        if (response.ok) {
          return { success: true, ...data }
        } else {
          throw new Error(data.detail || 'Failed to perform full bubble refresh')
        }
      } catch (error) {
        this.error = 'Error performing full bubble refresh'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async toggleReviewed(uuid: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/${
            this.bubble.reviewed ? 'mark_as_unreviewed' : 'mark_as_reviewed'
          }`,
          { method: 'PUT' }
        )
        const data = await response.json()
        if (response.ok) {
          this.bubble.reviewed = !this.bubble.reviewed
          // Update the bubbles store
          const bubblesStore = useBubblesStore()
          if (bubblesStore.bubbles[uuid]) {
            bubblesStore.bubbles[uuid].reviewed = this.bubble.reviewed
          }
          return { success: true, ...data }
        } else {
          throw new Error(data.detail || 'Failed to toggle reviewed state')
        }
      } catch (error) {
        this.error = 'Error toggling reviewed state'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async toggleArchive(uuid: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/${
            this.bubble.archived ? 'unarchive' : 'archive'
          }`,
          { method: 'PUT' }
        )
        const data = await response.json()
        if (response.ok) {
          this.bubble.archived = !this.bubble.archived
          // Update the bubbles store
          const bubblesStore = useBubblesStore()
          if (bubblesStore.bubbles[uuid]) {
            bubblesStore.bubbles[uuid].archived = this.bubble.archived
          }
          return { success: true, ...data }
        } else {
          throw new Error(data.detail || 'Failed to toggle archive state')
        }
      } catch (error) {
        this.error = 'Error toggling archive state'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },
    async updateMetaString(uuid: string, key: string, value: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${uuid}/update_meta_string`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ key, value }),
          }
        )
        const data = await response.json()
        if (response.ok) {
          // Update the local bubble state
          if (this.bubble && this.bubble.meta) {
            this.bubble.meta[key] = value
          }
          // Update the bubbles store
          const bubblesStore = useBubblesStore()
          if (bubblesStore.bubbles[uuid] && bubblesStore.bubbles[uuid].meta) {
            bubblesStore.bubbles[uuid].meta[key] = value
          }
          return {
            success: true,
            message: data.message,
            uuid: data.uuid,
            newValue: value,
          }
        } else {
          throw new Error(data.detail || `Failed to update meta string for key: ${key}`)
        }
      } catch (error) {
        this.error = `Error updating meta string: ${error instanceof Error ? error.message : String(error)}`
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },
  },
})
